/// *** System import ***///
import { Injectable, Inject } from '@angular/core';
import { Events, ToastController } from 'ionic-angular';
import { hubConnection } from 'signalr-no-jquery';
import { RESOURCE_CACHE_PROVIDER } from '@angular/platform-browser-dynamic';
/// *** Custom import ***///
import { Globals } from '../../app/globals';
import { EnvVariables } from '../../app/environment-variables/environment-variables.token';

@Injectable()
export class MarketSessionHubProvider {
  constructor(
    // *** System
    private events: Events,
    private toastCtrl: ToastController,
    // *** Custom
    private globals: Globals,
    @Inject(EnvVariables) private envVariables) {
  }

  // *** Hub subscription
  public subscribe(userID: string) {
    return new Promise((resolve, reject) => {
      try {
        // Init
        let connection = hubConnection(this.envVariables.serverPushEndpoint);
        connection.logging = false;
        let proxy = connection.createHubProxy('webmarketSession');
        // Subscriptions
        proxy.on('updateSessionStatus', this._onUpdateSessionStatus(this));
        proxy.on('updateSessionTrade', this._onUpdateSessionTrade(this));
        proxy.on('updateIndexSummary', this._onUpdateIndexSummary(this));
        proxy.on('updateTheoreticalOpeningPrice', this._onUpdateTheoreticalOpeningPrice(this));
        proxy.on('updateBestLimit', this._onUpdateBestLimit(this));
        proxy.on('updateStaticThresholds', this._onUpdateStaticThresholds(this));
        proxy.on('updateOpeningSummary', this._onUpdateOpeningSummary(this));
        proxy.on('updatePrice', this._onUpdatePrice(this));
        proxy.on('updateTrade', this._onUpdateTrade(this));
        proxy.on('updateInstrumentState', this._onUpdateInstrumentState(this));
        proxy.on('updateOrders', this._onUpdateOrders(this));
        // Start
        connection.start()
          .done(function () {
            proxy.invoke('subscribe', userID);
          })
          .fail(function (provider = this) {
            provider.events.publish('MarketSessionHubProvider.reconnect', userID);
          });
        // Keep alive
        connection.disconnected(function () {
        });
        // On try reconnect
        this.events.subscribe('MarketSessionHubProvider.reconnect',
          (userID) => {
            connection.stop();
            setTimeout((provider = this) => {
              provider.subscribe(userID);
            }, 5000);
          });
        // Abord connection
        this.events.subscribe('MarketSessionHubProvider.abord', () => { connection.stop(); });
        // On session user logout
        this.events.subscribe('SessionProvider.logout', () => { connection.stop(); });
        resolve();
      }
      catch (error) {
        this.globals.showCommunicationError(this.toastCtrl, error);
        reject();
      }
    });
  }

  // *** 'updateSessionStatus' handler
  private _onUpdateSessionStatus(provider: MarketSessionHubProvider): { (sessionDate: any, stateColor: any, state: any): void } {
    return function (sessionDate: any, stateColor: any, state: any) {
      provider.events.publish('MarketSessionHubProvider.updateSessionStatus', sessionDate, stateColor, state);
    }
  }

  // *** 'updateSessionTrade' handler
  private _onUpdateSessionTrade(provider: MarketSessionHubProvider): { (volume: any, numberOfTransactions: any): void } {
    return function (volume: any, numberOfTransactions: any) {
      provider.events.publish('MarketSessionHubProvider.updateSessionTrade', volume, numberOfTransactions);
    }
  }

  // *** 'updateIndexSummary' handler
  private _onUpdateIndexSummary(provider: MarketSessionHubProvider): { (indexData: any): void } {
    return function (indexData: any) {
      let index = JSON.parse(indexData);
      provider.events.publish('MarketSessionHubProvider.updateIndexSummary', index);
    }
  }

  // *** 'updateTheoreticalOpeningPrice' handler
  private _onUpdateTheoreticalOpeningPrice(provider: MarketSessionHubProvider): { (instrumentData: any): void } {
    return function (instrumentData: any) {
      let instrument = JSON.parse(instrumentData);
      provider.events.publish('MarketSessionHubProvider.updateTheoreticalOpeningPrice', instrument);
    }
  }

  // *** 'updateBestLimit' handler
  private _onUpdateBestLimit(provider: MarketSessionHubProvider): { (instrumentData: any): void } {
    return function (instrumentData: any) {
      let instrument = JSON.parse(instrumentData);
      provider.events.publish('MarketSessionHubProvider.updateBestLimit', instrument);
    }
  }

  // *** 'updateStaticThresholds' handler
  private _onUpdateStaticThresholds(provider: MarketSessionHubProvider): { (instrumentData: any): void } {
    return function (instrumentData: any) {
      let instrument = JSON.parse(instrumentData);
      provider.events.publish('MarketSessionHubProvider.updateStaticThresholds', instrument);
    }
  }

  // *** 'updateOpeningSummary' handler
  private _onUpdateOpeningSummary(provider: MarketSessionHubProvider): { (instrumentData: any): void } {
    return function (instrumentData: any) {
      let instrument = JSON.parse(instrumentData);
      provider.events.publish('MarketSessionHubProvider.updateOpeningSummary', instrument);
    }
  }

  // *** 'updatePrice' handler
  private _onUpdatePrice(provider: MarketSessionHubProvider): { (instrumentData: any): void } {
    return function (instrumentData: any) {
      let instrument = JSON.parse(instrumentData);
      provider.events.publish('MarketSessionHubProvider.updatePrice', instrument);
    }
  }

  // *** 'updateTrade' handler
  private _onUpdateTrade(provider: MarketSessionHubProvider): { (instrumentData: any): void } {
    return function (instrumentData: any) {
      let instrument = JSON.parse(instrumentData);
      provider.events.publish('MarketSessionHubProvider.updateTrade', instrument);
    }
  }

  // *** 'updateInstrumentState' handler
  private _onUpdateInstrumentState(provider: MarketSessionHubProvider): { (instrumentData: any): void } {
    return function (instrumentData: any) {
      let instrument = JSON.parse(instrumentData);
      provider.events.publish('MarketSessionHubProvider.updateInstrumentState', instrument);
    }
  }

  // *** 'updateOrders' handler
  private _onUpdateOrders(provider: MarketSessionHubProvider): { (orderData: any): void } {
    return function (orderData: any) {
      let order = JSON.parse(orderData);
      provider.events.publish('MarketSessionHubProvider.updateOrders', order);
    }
  }
}


// WEBPACK FOOTER //
// ./src/providers/serverHubs/marketSession.ts